import React, { useRef, useState, useEffect, useCallback } from "react";
import field from "../../images/field.webp";
import field1 from "../../images/field1.webp";
import field2 from "../../images/field2.webp";
import field3 from "../../images/field3.webp";
import field4 from "../../images/field4.webp";
import field5 from "../../images/field5.webp";
import field6 from "../../images/field6.webp";
import field7 from "../../images/field7.webp";
import field8 from "../../images/field8.webp";
import field9 from "../../images/field9.webp";
import field11 from "../../images/field11.webp";
import field12 from "../../images/field12.webp";
const fieldImages = [
  field,
  field1,
  field2,
  field3,
  field4,
  field5,
  field6,
  field7,
  field8,
  field9,
  field11,
  field12,
].map((src, index) => ({
  id: index + 1,
  src,
  alt: `Field installation ${index + 1}`,
}));

const AUTO_SLIDE_INTERVAL = 2500; // ms between auto-advances
const RESUME_DELAY = 3000; // ms after user interaction before auto-slide resumes

export default function FieldInstallation() {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  // Auto-slide control state
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const resumeTimeoutRef = useRef(null);
  const isUserInteractingRef = useRef(false);

  const checkScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeft(scrollLeft > 5);
    setShowRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  // Get one-card scroll amount from the real DOM (works at every breakpoint)
  const getScrollAmount = useCallback(() => {
    if (!scrollRef.current) return 600;
    const firstCard = scrollRef.current.querySelector("[data-card]");
    const cardWidth = firstCard ? firstCard.offsetWidth : 600;
    const gap =
      window.innerWidth >= 1280 ? 40 : window.innerWidth >= 768 ? 24 : 16;
    return cardWidth + gap;
  }, []);

  const scrollByAmount = useCallback(
    (direction) => {
      if (!scrollRef.current) return;
      const el = scrollRef.current;
      const amount = getScrollAmount();
      const maxScroll = el.scrollWidth - el.clientWidth;

      let nextLeft;
      if (direction === "left") {
        nextLeft = el.scrollLeft - amount;
        if (nextLeft < 0) nextLeft = 0;
      } else {
        nextLeft = el.scrollLeft + amount;
        // Loop back to start if we've hit (or passed) the end
        if (nextLeft >= maxScroll - 5) nextLeft = 0;
      }

      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    },
    [getScrollAmount],
  );

  // Pause auto-slide after any user interaction, then resume
  const pauseThenResume = useCallback(() => {
    isUserInteractingRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isUserInteractingRef.current = false;
    }, RESUME_DELAY);
  }, []);

  // Listen for scroll/resize
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      checkScroll();
      // Any scroll (including auto-scroll) delays resumption so we don't
      // immediately snap back while animation is still running
      pauseThenResume();
    };

    el.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", checkScroll);
    checkScroll();

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll, pauseThenResume]);

  // Auto-slide interval
  useEffect(() => {
    // Tab visibility — skip auto-advance when the tab is hidden
    const handleVisibility = () => {
      setIsPaused(document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    if (isPaused || isHovered) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const interval = setInterval(() => {
      if (isUserInteractingRef.current) return;
      if (isPaused || isHovered) return;
      scrollByAmount("right");
    }, AUTO_SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [isPaused, isHovered, scrollByAmount]);

  // Clean up the resume timer on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const handleArrowClick = (direction) => {
    pauseThenResume();
    scrollByAmount(direction);
  };

  return (
    <section className="relative w-full bg-[#101928] overflow-hidden pt-12 sm:pt-14 md:pt-16 pb-12 sm:pb-16 md:pb-20 xl:pb-[92px]">
      {/* ============ Header ============ */}
      <div className="w-full max-w-[717px] mx-auto flex flex-col items-center gap-4 px-4 sm:px-6 text-center">
        <h2
          className="text-[26px] sm:text-[32px] md:text-[36px] xl:text-[40px] font-semibold leading-[120%] tracking-[-0.04em] text-white"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontFeatureSettings: "'cv01' on, 'cv03' on, 'cv04' on",
          }}
        >
          Field Installations
        </h2>
        <p
          className="w-full max-w-[603px] text-[14px] sm:text-[16px] xl:text-[18px] leading-[21px] text-[#898989]"
          style={{ fontFamily: "'SF Pro Display', sans-serif" }}
        >
          Some of our recent field installation{" "}
        </p>
      </div>

      {/* ============ Carousel ============ */}
      <div
        className="relative mt-10 sm:mt-14 md:mt-16 xl:mt-[93px]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setIsHovered(false)}
      >
        {/* Left arrow */}
        <button
          type="button"
          onClick={() => handleArrowClick("left")}
          disabled={!showLeft}
          aria-label="Scroll left"
          className={`hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-black/60 transition-all ${
            !showLeft ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Right arrow */}
        <button
          type="button"
          onClick={() => handleArrowClick("right")}
          disabled={!showRight}
          aria-label="Scroll right"
          className={`hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-black/60 transition-all ${
            !showRight ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M7.5 5L12.5 10L7.5 15"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Scrollable image row */}
        <div
          ref={scrollRef}
          className="flex flex-row gap-4 md:gap-6 xl:gap-10 overflow-x-auto overflow-y-hidden scroll-smooth snap-x snap-mandatory no-scrollbar"
        >
          {fieldImages.map((img) => (
            <div
              key={img.id}
              data-card
              className="flex-shrink-0 w-[280px] sm:w-[360px] md:w-[440px] lg:w-[520px] xl:w-[600px] aspect-[600/541] rounded-[20px] overflow-hidden snap-start bg-[#1a2438]"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Hide scrollbar while keeping scroll functionality */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}
